/** PageGuard — wraps a page element and blocks rendering if the page is disabled.
 *
 * Usage: <PageGuard slug="marketplace"><MarketplacePage /></PageGuard>
 *
 * Behavior:
 *   - isLoading → show <LoadingBlock /> (non-blocking: never locks the app longer than the fetch).
 *   - canAccess(slug) → render children.
 *   - else → friendly EmptyState "This feature isn't enabled".
 *   - Locked or unknown slugs always render children (fail-open).
 */
import type { ReactNode } from "react";
import { usePermissions } from "../auth/PermissionsContext";
import { EmptyState } from "./EmptyState";
import { LoadingBlock } from "./Spinner";

interface PageGuardProps {
  slug: string;
  children: ReactNode;
}

export function PageGuard({ slug, children }: PageGuardProps) {
  const { canAccess, isLoading } = usePermissions();

  if (isLoading) {
    return <LoadingBlock />;
  }

  if (!canAccess(slug)) {
    return (
      <div className="page stack">
        <div className="card">
          <EmptyState title="This feature isn't enabled">
            Contact your admin to enable this feature.
          </EmptyState>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
